import moment from "moment-timezone";
import { ReactNode, useEffect, useState } from "react";
import "../index.css";
import SimpleSelect, {
  SimpleChoiceSelect,
  SimpleNumericCreatableSelect,
} from "components/Select/simple";
import { CreatableSelect } from "components/Select";
import {
  MeetingAdminResponse,
  MeetingLimitedResponse,
  MeetingTutorLimitedResponse,
  MeetingTutorAdminResponse,
  SessionFrequency,
  TutorAdminResponse,
  TutorLimitedResponse,
  OrganizationMeetingLocationResponse,
  OrganizationMeetingLocationType,
} from "client/openapi";
import type { BusyDate } from "client/openapi/models/BusyDate";
import type { TutorBusyDate } from "client/openapi/models/TutorBusyDate";
import AvailabilityDayPicker from "components/AvailabilityDayPicker";
import DayInput from "components/DayPicker";
import { FocusedWeek, MeetingDetails } from "..";
import { withTutorChoices } from "../meetingDraft";
import { AvailabilitiesService, LocationsService } from "client/openapi";
import { getAttendeeId } from "components/MeetingAttendees";
import { concatenateName } from "util/concatenateName";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import { FIELD_LABEL, GridViewButton } from "./MeetingFields";
import {
  CalendarIcon,
  Cross2Icon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "h:mma";

/** "A", "A and B", "A, B, and C" */
const listAnd = (items: string[]) =>
  items.length > 2
    ? `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
    : items.join(" and ");

/** "Thu, Nov 5", "Thu, Nov 5 and Thu, Nov 12", "Thu, Nov 5, Thu, Nov 12, and …" */
const listDates = (dates: string[]) =>
  listAnd(dates.map((date) => moment(date).format("ddd, MMM D")));

/** A time as people write it: "11 AM", "8:30 PM". */
const timeWords = (time: string) =>
  moment(time).format(moment(time).minutes() ? "h:mm A" : "h A");

export default function EditMeetingDetails({
  id,
  tutors,
  event,
  currentMeetingDetails,
  orginalMeetingDetails,
  saveMeetingDetails,
  locationOptions,
  creating,
  compact = false,
  onShowRooms,
  roomsShown = false,
  attendees,
  subject,
  focusedWeek,
  onFocusWeek,
}: {
  id: number;
  tutors: (
    | MeetingTutorLimitedResponse
    | MeetingTutorAdminResponse
    | TutorLimitedResponse
    | TutorAdminResponse
  )[];
  event?: MeetingAdminResponse | MeetingLimitedResponse;
  currentMeetingDetails: MeetingDetails;
  orginalMeetingDetails: MeetingDetails;
  saveMeetingDetails: (meeting: MeetingDetails) => void;
  locationOptions?: OrganizationMeetingLocationResponse[];
  creating: boolean;
  /**
   * For the calendar's booking pop-up: no Date, Time, Duration or Notes
   * (the calendar selection sets the time) and no gray panel. Repeats,
   * Ends on or before and Location work exactly as in Book Meeting.
   */
  compact?: boolean;
  /** Shows the rooms icon beside Location when provided. */
  onShowRooms?: () => void;
  roomsShown?: boolean;
  /** The meeting's tutors and students: the field grid's first two cells. */
  attendees?: ReactNode;
  /** The meeting's subject: the cell beside Location. */
  subject?: ReactNode;
  /** The week of a new series shown in the tutors' grid beside the form. */
  focusedWeek?: FocusedWeek | null;
  /** A click on a week shows it in that grid (again: no week). */
  onFocusWeek?: (week: FocusedWeek | null) => void;
}) {
  const [locationAvailability, setLocationAvailability] = useState<{
    available?: boolean;
    availableLocations?: OrganizationMeetingLocationResponse[];
    busyDates?: BusyDate[];
    seriesLength?: number;
  }>({});
  const [tutorAvailability, setTutorAvailability] = useState<{
    busyDates?: TutorBusyDate[];
    seriesLength?: number;
    seriesStarts?: string[];
    withoutHours?: number[];
  }>({});
  // The week whose free times show, when there is no tutors' grid to focus,
  // and the week a time or Skip was just picked for (its times close).
  const [shownWeek, setShownWeek] = useState<string | null>(null);
  const [pickedWeek, setPickedWeek] = useState<string | null>(null);
  // A change to the time, length, repeat or place makes the rooms and times
  // chosen for busy weeks stale.
  const saveDetails = (meeting: MeetingDetails) =>
    saveMeetingDetails({
      ...meeting,
      room_overrides: undefined,
      tutor_overrides: undefined,
    });

  const getStartTime = () =>
    moment
      .tz(currentMeetingDetails.start, moment.tz.guess())
      .format(TIME_FORMAT);

  const handleChangeStartDate = (date: Date) => {
    const currentTime = moment
      .tz(currentMeetingDetails.start, moment.tz.guess())
      .format("HH:mm");

    // Format the new date in YYYY-MM-DD
    const newDate = moment.tz(date, moment.tz.guess()).format("YYYY-MM-DD");

    // Combine them in local time
    const combined = moment.tz(
      `${newDate} ${currentTime}`,
      "YYYY-MM-DD HH:mm",
      moment.tz.guess()
    );

    // Store as ISO string
    const updatedMeeting = {
      ...currentMeetingDetails,
      start: combined.toISOString(),
    };

    saveDetails(updatedMeeting);
  };

  // --- 4) Handle end date changes (unchanged) ---
  const handleChangeEndDate = (date: Date) => {
    let updatedMeeting: MeetingDetails;

    if (currentMeetingDetails.frequency === SessionFrequency.SINGLE) {
      updatedMeeting = {
        ...currentMeetingDetails,
        ends_on_or_before: undefined,
      };
    } else {
      updatedMeeting = {
        ...currentMeetingDetails,
        ends_on_or_before: moment.tz(date, moment.tz.guess()).toISOString(),
      };
    }
    saveDetails(updatedMeeting);
  };

  // --- 5) Handle time changes (no manual DST offset) ---
  const handleChangeStartTime = (ev: { value: string }) => {
    // Example: ev.value might be "5:00pm" given TIME_FORMAT = "h:mma"
    // Grab the DATE from existing start
    const datePart = moment
      .tz(currentMeetingDetails.start, moment.tz.guess())
      .format("YYYY-MM-DD");

    // Combine them in local time
    const combined = moment.tz(
      `${datePart} ${ev.value}`,
      "YYYY-MM-DD h:mma",
      moment.tz.guess()
    );

    // Store as ISO
    const updatedMeeting = {
      ...currentMeetingDetails,
      start: combined.toISOString(),
    };
    saveDetails(updatedMeeting);
  };

  // --- 6) Handle duration changes (unchanged) ---
  const handleChangeDuration = (ev: { value: number }) => {
    const updatedMeeting = {
      ...currentMeetingDetails,
      duration: ev.value,
    };
    saveDetails(updatedMeeting);
  };

  // --- 7) Build local time‐options (unchanged) ---
  const getTimeOptions = () => {
    const options: { label: string; value: string }[] = [];
    for (let i = 0; i < 24; i++) {
      [0, 15, 30, 45].forEach((m) => {
        // Format in local 12‐hour style
        const timeLabel = moment({ hour: i, minute: m }).format(TIME_FORMAT);
        options.push({ label: timeLabel, value: timeLabel });
      });
    }
    return options;
  };

  // Repeat frequency (unchanged)
  const getRepeatOptions = () => [
    { value: SessionFrequency.SINGLE, label: "Never" },
    { value: SessionFrequency.EVERY_7_DAYS, label: "Every 7 days" },
    { value: SessionFrequency.EVERY_14_DAYS, label: "Every 14 days" },
    { value: SessionFrequency.EVERY_21_DAYS, label: "Every 21 days" },
    { value: SessionFrequency.EVERY_28_DAYS, label: "Every 28 days" },
  ];

  const changeFrequency = (frequency: { value: SessionFrequency }) => {
    const updatedMeeting = {
      ...currentMeetingDetails,
      frequency: frequency.value,
    };
    saveDetails(updatedMeeting);
  };

  // Check location availability whenever relevant fields change. Only the
  // newest answer counts.
  useEffect(() => {
    const location = currentMeetingDetails.location;
    const meetingStart = moment.tz(
      currentMeetingDetails.start,
      moment.tz.guess()
    );
    setLocationAvailability({});
    if (!location?.id || !meetingStart.isValid()) return;

    let cancelled = false;
    LocationsService.getLocationAvailability({
      desiredLocationId: location.id,
      orgId: currentMeetingDetails.org_id,
      desiredStart: meetingStart.toISOString(),
      desiredEnd: meetingStart
        .clone()
        .add(currentMeetingDetails.duration, "minutes")
        .toISOString(),
      editingMeetingId: orginalMeetingDetails.meeting_id ?? undefined,
      // A new repeating meeting is checked on every date of the series.
      ...(creating &&
        currentMeetingDetails.ends_on_or_before && {
          frequency: currentMeetingDetails.frequency,
          endsOnOrBefore: moment(currentMeetingDetails.ends_on_or_before)
            .endOf("day")
            .utc()
            .format(),
        }),
    })
      .then((resp) => {
        if (cancelled) return;
        setLocationAvailability({
          available: resp.desired_location_available,
          availableLocations: resp.available_locations,
          busyDates: resp.busy_dates,
          seriesLength: resp.series_length,
        });
      })
      .catch(() => {
        if (!cancelled) setLocationAvailability({});
      });
    return () => {
      cancelled = true;
    };
  }, [
    currentMeetingDetails.location,
    currentMeetingDetails.start,
    currentMeetingDetails.duration,
    currentMeetingDetails.org_id,
    currentMeetingDetails.frequency,
    currentMeetingDetails.ends_on_or_before,
    orginalMeetingDetails.meeting_id,
    creating,
  ]);

  // A new repeating meeting's tutors are checked on every date of the series,
  // as the public booking page counts them. Only the newest answer counts.
  const tutorKey = currentMeetingDetails.tutor_ids.join(",");
  const inPersonId =
    currentMeetingDetails.location?.location_type ===
    OrganizationMeetingLocationType.IN_PERSON
      ? currentMeetingDetails.location.id
      : undefined;
  useEffect(() => {
    const meetingStart = moment.tz(
      currentMeetingDetails.start,
      moment.tz.guess()
    );
    setTutorAvailability({});
    if (
      !creating ||
      !tutorKey ||
      !currentMeetingDetails.duration ||
      !meetingStart.isValid() ||
      currentMeetingDetails.frequency === SessionFrequency.SINGLE ||
      !currentMeetingDetails.ends_on_or_before
    )
      return;

    let cancelled = false;
    AvailabilitiesService.getTutorsSeriesAvailability({
      orgId: currentMeetingDetails.org_id,
      tutorIds: tutorKey.split(",").map(Number),
      start: meetingStart.toISOString(),
      duration: currentMeetingDetails.duration,
      frequency: currentMeetingDetails.frequency,
      endsOnOrBefore: moment(currentMeetingDetails.ends_on_or_before)
        .endOf("day")
        .utc()
        .format(),
      locationId: inPersonId,
    })
      .then((resp) => {
        if (cancelled) return;
        setTutorAvailability({
          busyDates: resp.busy_dates,
          seriesLength: resp.series_length,
          seriesStarts: resp.series_starts,
          withoutHours: resp.tutors_without_hours,
        });
      })
      .catch(() => {
        if (!cancelled) setTutorAvailability({});
      });
    return () => {
      cancelled = true;
    };
  }, [
    tutorKey,
    inPersonId,
    currentMeetingDetails.start,
    currentMeetingDetails.duration,
    currentMeetingDetails.org_id,
    currentMeetingDetails.frequency,
    currentMeetingDetails.ends_on_or_before,
    creating,
  ]);

  // The tutors' choice for busy weeks, and the weeks the room's choice skips.
  const tutorOverrides = currentMeetingDetails.tutor_overrides ?? [];
  const tutorChosen = new Set(tutorOverrides.map((override) => override.date));
  const tutorSkips = tutorOverrides.filter((override) => !override.start);
  const roomSkipped = new Set(
    (currentMeetingDetails.room_overrides ?? [])
      .filter((override) => !override.location_id)
      .map((override) => override.date)
  );

  // A new repeating meeting in an in-person place: its busy dates, and a free
  // in-person place for each.
  const checksSeries =
    creating &&
    !!currentMeetingDetails.ends_on_or_before &&
    currentMeetingDetails.frequency !== SessionFrequency.SINGLE &&
    currentMeetingDetails.location?.location_type ===
      OrganizationMeetingLocationType.IN_PERSON;
  const roomBusyAll = checksSeries ? locationAvailability.busyDates ?? [] : [];
  // A week the tutors' choice moved or skipped is not the room's to fix: a
  // moved week was checked free in this room.
  const busyDates = roomBusyAll.filter((busy) => !tutorChosen.has(busy.date));
  const busyStarts = busyDates.map((busy) => busy.start);
  const freeRooms = busyDates.map((busy) =>
    locationOptions?.find((location) => location.id === busy.free_location_id)
  );

  // A new repeating meeting with tutors: every date of the series, with the
  // tutors busy then (if any) and the choice made for it.
  const checksTutorSeries =
    creating &&
    !!currentMeetingDetails.ends_on_or_before &&
    currentMeetingDetails.frequency !== SessionFrequency.SINGLE &&
    currentMeetingDetails.tutor_ids.length > 0;
  const tutorBusy = checksTutorSeries ? tutorAvailability.busyDates ?? [] : [];
  const weeks = (
    checksTutorSeries ? tutorAvailability.seriesStarts ?? [] : []
  ).map((start) => {
    const busy = tutorBusy.find((busyDate) => busyDate.start === start);
    const date = busy?.date ?? moment(start).format(DATE_FORMAT);
    const choice = busy && tutorOverrides.find((o) => o.date === date);
    return {
      start,
      busy,
      date,
      choice,
      // By the tutors' choice, or the room's (then not the tutors' to fix).
      skipped: roomSkipped.has(date) || choice?.start === null,
    };
  });
  // The busy weeks no choice covers yet.
  const openWeeks = weeks.filter(
    (week) => week.busy && !week.choice && !week.skipped
  );
  const skippedWeeks = weeks.filter((week) => week.skipped).length;
  const nameOf = (tutorId: number) => {
    const tutor = tutors.find((t) => getAttendeeId(t) === tutorId);
    return tutor ? concatenateName(tutor) : "A tutor";
  };
  const tutorNames = currentMeetingDetails.tutor_ids.map(nameOf);
  const withoutHours = (tutorAvailability.withoutHours ?? []).map(nameOf);

  // The week a tile shows: the grid's focus when there is a grid, else its
  // own. A busy one (not the room's to skip) shows its free times.
  const shown = onFocusWeek ? focusedWeek?.start ?? null : shownWeek;
  const showWeek = (week: (typeof weeks)[number] | null) =>
    onFocusWeek
      ? onFocusWeek(
          week ? { start: week.start, busyDate: week.busy?.date } : null
        )
      : setShownWeek(week ? week.start : null);
  const shownBusy = weeks.find(
    (week) =>
      week.start === shown &&
      week.start !== pickedWeek &&
      week.busy &&
      !roomSkipped.has(week.date)
  );
  // A pick for the shown week, and its times close; the grid stays on it.
  const pickForShown = (date: string, start: string | null) => {
    chooseForTutors(date, start);
    setPickedWeek(shown);
  };
  // Starts by part of the day, each part one row of chips.
  const partRows = (starts: string[], chip: (start: string) => ReactNode) =>
    (
      [
        ["Morning", (hour: number) => hour < 12],
        ["Afternoon", (hour: number) => hour >= 12 && hour < 17],
        ["Evening", (hour: number) => hour >= 17],
      ] as const
    )
      .map(
        ([part, inPart]) =>
          [part, starts.filter((s) => inPart(moment(s).hour()))] as const
      )
      .filter(([, times]) => times.length > 0)
      .map(([part, times]) => (
        <div key={part} className="mt-2 flex items-baseline gap-3">
          <span className="w-16 shrink-0 text-gray-500">{part}</span>
          <div className="flex flex-wrap gap-1.5">{times.map(chip)}</div>
        </div>
      ));
  const isAre = (count: number) => (count > 1 ? "are" : "is");

  // The tutors' choice for one busy week: another start that day, or none to
  // skip it.
  const chooseForTutors = (date: string, start: string | null) =>
    saveMeetingDetails(
      withTutorChoices(currentMeetingDetails, [{ date, start }])
    );
  // No choice for the week again. The room's choice no longer covers it if
  // the week is busy for the room again.
  const undoTutorChoice = (date: string) => {
    const rest = tutorOverrides.filter((override) => override.date !== date);
    saveMeetingDetails({
      ...currentMeetingDetails,
      tutor_overrides: rest.length ? rest : undefined,
      room_overrides: roomBusyAll.some((busy) => busy.date === date)
        ? undefined
        : currentMeetingDetails.room_overrides,
    });
  };

  // --- 9) Return JSX ---
  // One column of rows: Date, Time and Duration; Repeats; the field grid;
  // Notes (editing). A plain column, so no row can split it into columns.
  return (
    <div
      className={
        compact
          ? "flex flex-col"
          : "flex flex-col bg-gray-100 rounded-lg p-4 mr-2"
      }
    >
      {!compact && (
        <div className="flex flex-wrap gap-2 text-sm">
          {/* DATE */}
          <div className="mr-2 mt-1">
            <p className="text-xs font-bold">Date</p>
            <AvailabilityDayPicker
              id={"start-date" + id}
              event={event}
              handleChangeStartDate={handleChangeStartDate}
              tutors={tutors}
              meetingDetails={currentMeetingDetails}
              required
              classes="py-2 px-1 m-0 border-green-300 border-0 border-b-2 bg-transparent text-sm"
            />
          </div>
          {/* START TIME */}
          <div className="mr-2 mt-1">
            <p className="text-xs font-bold">Time</p>
            <SimpleSelect
              id="start-time"
              options={getTimeOptions()}
              value={{
                value: getStartTime(),
                label: getStartTime(),
              }}
              placeholder="XX:XXam"
              onChange={handleChangeStartTime}
              required
            />
          </div>
          {/* DURATION */}
          <div className="mr-2 mt-1">
            <p className="text-xs font-bold">Duration</p>
            <SimpleNumericCreatableSelect
              options={Array.from({ length: 11 }, (_, i) => ({
                value: (i + 1) * 15,
                label: `${(i + 1) * 15} min`,
              }))}
              value={{
                label: `${currentMeetingDetails.duration} min`,
                value: currentMeetingDetails.duration,
              }}
              onChange={handleChangeDuration}
            />
          </div>
        </div>
      )}

      {/* Repeats on its own row, with the last date beside it when it repeats. */}
      <div className={`flex flex-wrap gap-2 text-sm ${compact ? "" : "mt-2"}`}>
        {/* REPEATS */}
        <div className="mr-2 mt-1">
          <p className="text-xs font-bold">Repeats</p>
          <SimpleChoiceSelect
            id="repeat-on"
            options={getRepeatOptions()}
            value={getRepeatOptions().find(
              (opt) => opt.value === currentMeetingDetails.frequency
            )}
            onChange={changeFrequency}
            isDisabled={!!event}
            required
            {...(compact ? { menuPlacement: "auto" } : {})}
          />
        </div>
        {/* ENDS ON OR BEFORE */}
        {currentMeetingDetails.frequency !== SessionFrequency.SINGLE && (
          <div className="mt-1">
            <p className="text-xs font-bold">Ends on or before</p>
            <DayInput
              id={"end-date" + id}
              value={
                currentMeetingDetails.ends_on_or_before
                  ? moment
                      .tz(
                        currentMeetingDetails.ends_on_or_before,
                        moment.tz.guess()
                      )
                      .toISOString()
                  : ""
              }
              classes="py-2 px-1 m-0 border-green-300 border-0 border-b-2 bg-transparent text-sm"
              // The pop-up has no inner scroll, so its calendar opens upward.
              dayPickerClasses={compact ? "bottom-full" : undefined}
              fixedWeeks={compact}
              setValue={handleChangeEndDate}
              required
              min={moment
                .tz(currentMeetingDetails.start, moment.tz.guess())
                .add(1, "day")
                .format(DATE_FORMAT)}
            />
          </div>
        )}
      </div>

      {/* The field grid: Tutors and Students, then Location and Subject, all
          one width (two rows in the pop-up too). */}
      <div
        className={`meeting-fields grid gap-x-4 gap-y-3 ${
          compact ? "mt-3 grid-cols-2" : "mt-4 sm:grid-cols-2"
        }`}
      >
        {attendees}
        <div className="relative min-w-0">
          <div className={FIELD_LABEL}>
            Location
            {currentMeetingDetails.location &&
              !checksSeries &&
              locationAvailability.available === true && (
                <span className="text-green-600">available</span>
              )}
            {currentMeetingDetails.location &&
              !checksSeries &&
              locationAvailability.available === false && (
                <span className="flex items-center gap-1">
                  <span className="text-red-600">not available</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoCircledIcon className="text-gray-500 hover:text-gray-700 cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Available locations:
                      <ul className="list-disc ml-1 mt-2 text-xs">
                        {locationAvailability.availableLocations?.map((loc) => (
                          <li key={loc.id} className="text-sm">
                            • {loc.name}
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </span>
              )}
            {onShowRooms &&
              locationOptions?.some(
                (location) =>
                  location.location_type ===
                  OrganizationMeetingLocationType.IN_PERSON
              ) && (
                <GridViewButton
                  label="Room availability"
                  active={roomsShown}
                  onClick={onShowRooms}
                />
              )}
          </div>
          <LocationSelect
            id="location-select"
            locationOptions={locationOptions}
            meeting={currentMeetingDetails}
            onChange={(meeting) => {
              saveDetails(meeting);
              if (!meeting.location) setLocationAvailability({});
            }}
            compact={compact}
          />
          {checksSeries &&
            roomBusyAll.length === 0 &&
            !!locationAvailability.seriesLength && (
              <p className="mt-1 text-xs font-semibold text-green-700">
                {currentMeetingDetails.location?.name} is free on all{" "}
                {locationAvailability.seriesLength} dates.
              </p>
            )}
          {busyDates.length > 0 && (
            <div className="mt-1 text-xs">
              {currentMeetingDetails.room_overrides ? (
                <p className="font-semibold text-green-700">
                  {currentMeetingDetails.room_overrides.every(
                    (override) => !override.location_id
                  )
                    ? `Skipping ${listDates(busyStarts)}.`
                    : busyDates
                        .map(
                          (busy, i) =>
                            `${moment(busy.start).format("ddd, MMM D")}: ${
                              freeRooms[i]?.name
                            }`
                        )
                        .join(". ") + "."}{" "}
                  <button
                    type="button"
                    className="font-bold text-gray-500 hover:underline"
                    onClick={() =>
                      saveMeetingDetails({
                        ...currentMeetingDetails,
                        room_overrides: undefined,
                      })
                    }
                  >
                    Undo
                  </button>
                </p>
              ) : (
                <>
                  <p className="font-semibold text-red-500">
                    {currentMeetingDetails.location?.name} is busy on{" "}
                    {listDates(busyStarts)}.
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {freeRooms.every(Boolean) && (
                      <Button
                        size={ButtonSize.EXTRA_SMALL}
                        color={ButtonColor.GREEN}
                        onClick={() =>
                          saveMeetingDetails({
                            ...currentMeetingDetails,
                            room_overrides: busyDates.map((busy, i) => ({
                              date: busy.date,
                              location_id: freeRooms[i]?.id,
                            })),
                          })
                        }
                      >
                        {new Set(freeRooms.map((room) => room?.id)).size === 1
                          ? `Use ${freeRooms[0]?.name} those weeks`
                          : "Use free rooms those weeks"}
                      </Button>
                    )}
                    {busyDates.length + tutorSkips.length <
                      (locationAvailability.seriesLength ?? 0) && (
                      <Button
                        size={ButtonSize.EXTRA_SMALL}
                        color={ButtonColor.GREEN}
                        fill={ButtonFill.HOLLOW}
                        onClick={() =>
                          saveMeetingDetails({
                            ...currentMeetingDetails,
                            room_overrides: busyDates.map((busy) => ({
                              date: busy.date,
                              location_id: null,
                            })),
                          })
                        }
                      >
                        Skip those weeks
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {subject}
      </div>
      {/* The tutors on every date of a new series, under the whole grid, since
          extra sessions have no Tutors cell. */}
      {checksTutorSeries && !!tutorAvailability.seriesLength && (
        <div className="mt-1 text-xs">
          {tutorBusy.length === 0 && (
            <p className="font-semibold text-green-700">
              {listAnd(tutorNames)} {tutorNames.length > 1 ? "are" : "is"} free
              on all {tutorAvailability.seriesLength} dates.
            </p>
          )}
          {weeks.length > 0 && tutorBusy.length > 0 && (
            // A click here picks a week or a time, not the form.
            <div onClick={(e) => e.stopPropagation()}>
              <p
                className={`font-semibold ${
                  openWeeks.length > 0 ? "text-red-500" : "text-green-700"
                }`}
              >
                {openWeeks.length > 0
                  ? `${weeks.length - openWeeks.length} of ${
                      weeks.length
                    } weeks work.`
                  : `All ${weeks.length} weeks work.`}
                {openWeeks.length > 0 && (
                  <span className="font-normal text-gray-600">
                    {" "}
                    Click a red week to see other times.
                  </span>
                )}
              </p>
              {/* A tile per week. A click shows its day in the tutors' grid
                  and, for a busy week, its free times below. */}
              <div className="mt-1 flex flex-wrap items-start gap-2">
                {weeks.map((week) => {
                  const moved = !!week.choice?.start;
                  const focused = shown === week.start;
                  const busyNames = listAnd(
                    (week.busy?.tutor_ids ?? []).map(nameOf)
                  );
                  return (
                    <div key={week.start} className="relative w-20">
                      <button
                        type="button"
                        className={`w-full rounded-md border px-1.5 py-1 text-left leading-tight transition hover:-translate-y-px hover:shadow-md ${
                          week.skipped
                            ? "border-gray-200 bg-gray-100 text-gray-500"
                            : moved
                            ? "border-green-500 bg-green-100 text-green-800"
                            : week.busy
                            ? "border-red-300 bg-red-50 text-red-700"
                            : "border-green-200 bg-green-50 text-green-700"
                        } ${
                          focused ? "ring-2 ring-green-600 ring-offset-1" : ""
                        }`}
                        title={
                          week.busy
                            ? `${busyNames} ${
                                week.busy.tutor_ids.length > 1 ? "are" : "is"
                              } busy at ${moment(week.start).format(
                                "h:mm A"
                              )} on ${moment(week.start).format("ddd, MMM D")}.`
                            : undefined
                        }
                        aria-pressed={focused}
                        onClick={() => {
                          // Shown with its times closed: they open again.
                          const reopen = focused && pickedWeek === week.start;
                          setPickedWeek(null);
                          if (!reopen) showWeek(focused ? null : week);
                        }}
                      >
                        <span className="flex items-center gap-1 font-semibold">
                          <CalendarIcon
                            className="h-3 w-3 shrink-0"
                            aria-hidden
                          />
                          {moment(week.start).format("MMM D")}
                        </span>
                        <span
                          className={`block ${
                            week.skipped ? "line-through" : ""
                          }`}
                        >
                          {timeWords(week.choice?.start ?? week.start)}
                        </span>
                        {(week.skipped || moved) && (
                          <span className="block">
                            {week.skipped ? "Skipped" : "Moved"}
                          </span>
                        )}
                      </button>
                      {week.choice && (
                        <button
                          type="button"
                          aria-label={`Undo ${moment(week.start).format(
                            "MMM D"
                          )}`}
                          title="Undo"
                          className="absolute right-0.5 top-0.5 rounded p-0.5 text-gray-500 hover:bg-white hover:text-gray-700"
                          onClick={() => undoTutorChoice(week.date)}
                        >
                          <Cross2Icon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* The shown busy week's free times that day, by part of the
                  day, and Skip. */}
              {shownBusy && (
                <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
                  <p className="font-semibold text-gray-800">
                    Free on {moment(shownBusy.start).format("dddd, MMMM D")}
                  </p>
                  <p className="text-gray-500">
                    {listAnd((shownBusy.busy?.tutor_ids ?? []).map(nameOf))}{" "}
                    {isAre(shownBusy.busy?.tutor_ids.length ?? 0)} busy at{" "}
                    {timeWords(shownBusy.start)}.
                  </p>
                  {/* Times every tutor is free: a pick moves the week. */}
                  {tutorNames.length > 1 && (
                    <p className="mt-3 font-semibold text-gray-700">
                      {listAnd(tutorNames)} {isAre(tutorNames.length)} free
                    </p>
                  )}
                  {(shownBusy.busy?.free_starts ?? []).length === 0 && (
                    <p className="mt-1 text-gray-500">
                      {tutorNames.length > 1
                        ? "No time works for all of them that day."
                        : "No free time that day."}
                    </p>
                  )}
                  {partRows(shownBusy.busy?.free_starts ?? [], (freeStart) => {
                    const chosen =
                      !!shownBusy.choice?.start &&
                      moment(shownBusy.choice.start).isSame(freeStart);
                    return (
                      <button
                        key={freeStart}
                        type="button"
                        aria-pressed={chosen}
                        title={chosen ? "Undo" : undefined}
                        className={`rounded-full border px-2.5 py-0.5 font-semibold tabular-nums ${
                          chosen
                            ? "border-green-600 bg-green-600 text-white"
                            : "border-green-300 bg-white text-green-700 hover:bg-green-50"
                        }`}
                        onClick={() =>
                          chosen
                            ? undoTutorChoice(shownBusy.date)
                            : pickForShown(shownBusy.date, freeStart)
                        }
                      >
                        {timeWords(freeStart)}
                      </button>
                    );
                  })}
                  {/* Times only some are free: shown, not picked. */}
                  {(shownBusy.busy?.free_for_some ?? []).map((group) => (
                    <div key={group.tutor_ids.join(",")}>
                      <p className="mt-3 font-semibold text-gray-500">
                        Only {listAnd(group.tutor_ids.map(nameOf))}{" "}
                        {isAre(group.tutor_ids.length)} free
                      </p>
                      {partRows(group.starts, (freeStart) => (
                        <span
                          key={freeStart}
                          className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 font-semibold tabular-nums text-gray-500"
                        >
                          {timeWords(freeStart)}
                        </span>
                      ))}
                    </div>
                  ))}
                  {/* Not the last week that still meets. */}
                  {(shownBusy.choice?.start === null ||
                    skippedWeeks + 1 < weeks.length) && (
                    <div className="mt-3 border-t border-gray-100 pt-2">
                      <button
                        type="button"
                        aria-pressed={shownBusy.choice?.start === null}
                        className={`rounded-full px-2.5 py-0.5 font-semibold ${
                          shownBusy.choice?.start === null
                            ? "bg-gray-500 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        onClick={() =>
                          shownBusy.choice?.start === null
                            ? undoTutorChoice(shownBusy.date)
                            : pickForShown(shownBusy.date, null)
                        }
                      >
                        {shownBusy.choice?.start === null
                          ? "Skipped. Undo"
                          : "Skip this week"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {withoutHours.length > 0 && (
            <p className="text-gray-500">
              {listAnd(withoutHours)}{" "}
              {withoutHours.length > 1 ? "haven't" : "hasn't"} set weekly hours,
              so no time is free for them.
            </p>
          )}
        </div>
      )}
      {!creating && (
        <div className="mt-4">
          <p className="pb-2 text-xs font-bold">Notes</p>
          <textarea
            id="notes"
            name="notes"
            className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none"
            placeholder="Add any notes about this meeting..."
            value={currentMeetingDetails.note ?? ""}
            onChange={(e) => {
              const textarea = e.target;
              textarea.style.height = "auto";
              textarea.style.height = `${textarea.scrollHeight}px`;
              const updatedMeeting = {
                ...currentMeetingDetails,
                note: e.target.value,
              };
              saveMeetingDetails(updatedMeeting);
            }}
            style={{ height: "auto", minHeight: "150px" }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Book Meeting's Location: one of the organization's places, or a custom one
 * typed in. Also in the rooms grid before a place is chosen.
 */
export function LocationSelect({
  id,
  locationOptions,
  meeting,
  onChange,
  compact = false,
}: {
  id: string;
  locationOptions?: OrganizationMeetingLocationResponse[];
  meeting: MeetingDetails;
  /** The meeting with its new place. */
  onChange: (meeting: MeetingDetails) => void;
  /** Menus open upward when there is no room below (the pop-up). */
  compact?: boolean;
}) {
  const value = meeting.location
    ? {
        value: meeting.location.id.toString(),
        label: meeting.location.name,
        __isNew__: false,
      }
    : meeting.other_location
    ? {
        value: meeting.other_location,
        label: meeting.other_location,
        __isNew__: true,
      }
    : null;

  return (
    <CreatableSelect
      id={id}
      // Sized with the other fields (meetingFields.css).
      classNamePrefix="meeting-field"
      placeholder="Select or type a location..."
      options={locationOptions?.map((loc) => ({
        ...loc,
        value: loc.id,
        label: loc.name,
      }))}
      value={value}
      onChange={(newValue: any) =>
        onChange(
          locationOptions && newValue && !newValue.__isNew__
            ? { ...meeting, location: newValue, other_location: undefined }
            : newValue && newValue.__isNew__
            ? // A custom place.
              {
                ...meeting,
                location: undefined,
                other_location: newValue.value,
              }
            : // Cleared.
              { ...meeting, location: undefined, other_location: "" }
        )
      }
      isClearable
      isSearchable
      formatCreateLabel={(inputValue) => `Use custom location "${inputValue}"`}
      allowCreateWhileLoading={false}
      components={{
        DropdownIndicator: undefined,
      }}
      {...(compact ? { menuPlacement: "auto" } : {})}
    />
  );
}
